import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import SparkMD5 from 'spark-md5';
import { ApiService } from './api.service';
import { UserStateService } from './user-state.service';
import { PathService } from './path.service';
import { DraftData, DraftInfo, DraftFinishFile, DraftFinishFiles } from '../models/product.model';

/** Per-call options for `loadDraft`. */
export interface LoadDraftOptions {
  /**
   * Append a cache-bust query param to the .order URL (and the API metadata
   * fallback) so the browser HTTP cache can't serve a stale response.
   *
   * Defaults to `true`. The `.order` file is rewritten on every draft
   * autosave and the URL is stable, so without cache-busting the browser
   * can hand back a pre-autosave snapshot — causing the post-Save&Exit
   * F5-restores-old-version bug. Pass `false` ONLY for read-only flows
   * where staleness is acceptable (none currently identified).
   */
  bustCache?: boolean;
}

/**
 * Service for managing draft orders (save/load/list/delete)
 * Drafts are saved as JSON instead of encrypted binary files
 */
@Injectable({
  providedIn: 'root'
})
export class DraftService {
  private readonly api = inject(ApiService);
  private readonly http = inject(HttpClient);
  private userState = inject(UserStateService);
  private pathService = inject(PathService);

  /**
   * Save a draft order to the server
   * @param draftName - User-provided name for the draft
   * @param draftData - Complete draft configuration
   * @param productTypeId - Product type ID (1 for albums, etc.)
   * @param draftId - Optional draft ID for updating existing draft
   * @param serverName - Optional server name for updating existing draft
   * @returns Observable<DraftInfo> - Draft metadata from server
   */
  saveDraft(
    draftName: string,
    draftData: DraftData,
    productTypeId: number,
    draftId?: number,
    serverName?: string,
    favorite: boolean = false
  ): Observable<DraftInfo> {
    const user = this.userState.user();

    if (!user?.id) {
      throw new Error('User not authenticated');
    }

    const formData = this.buildDraftOrderFormData(draftName, draftData, productTypeId, draftId, favorite);

    console.log('[DraftService] Saving draft via ApiService');
    console.log('[DraftService] Draft ID:', draftId, 'ServerName:', serverName);

    const path = draftId && draftId > 0
      ? `/upload/draft-orders-bytes/${draftId}`
      : '/upload/draft-orders-bytes';
    return this.api.upload<DraftInfo>(path, formData).pipe(
      map(data => {
        console.log('[DraftService] Draft saved:', data);
        return data;
      })
    );
  }

  /**
   * Build the multipart FormData for a draft-order upload. Extracted so the
   * Album Design area (POST /upload/album-design-bytes) reuses the EXACT same
   * field contract — the backend routes both through uploadDraftOrderBytes,
   * differing only by URL. Pure/stateless: no auth or network here.
   */
  buildDraftOrderFormData(
    draftName: string,
    draftData: DraftData,
    productTypeId: number,
    draftId?: number,
    favorite: boolean = false
  ): FormData {
    // Strip draftThumbnail from JSON — it's uploaded separately to S3
    const { draftThumbnail: _thumb, ...draftDataForJson } = draftData;
    const jsonContent = JSON.stringify(draftDataForJson, null, 2);

    // Backend expects bytes as base64 string (not binary file upload)
    const base64Bytes = btoa(unescape(encodeURIComponent(jsonContent)));

    // Compute MD5 of the raw content (backend decodes base64 first, then checks MD5)
    const md5 = new SparkMD5().append(jsonContent).end();

    // Create FormData
    const formData = new FormData();
    formData.append('bytes', base64Bytes);
    formData.append('md5', md5);
    formData.append('fileSize', base64Bytes.length.toString());
    formData.append('productTypeId', productTypeId.toString());
    formData.append('originalName', draftName);
    formData.append('id', draftId ? draftId.toString() : '0');
    // Collect project references from draft data
    const projects: Array<{ id: number; serverName: string }> = [];
    if (draftData.albumConfig) {
      if (draftData.albumConfig.albumProjectId) {
        projects.push({
          id: draftData.albumConfig.albumProjectId,
          serverName: draftData.albumConfig.albumProjectServerName || ''
        });
      }
      if (draftData.albumConfig.coverProjectId) {
        projects.push({
          id: draftData.albumConfig.coverProjectId,
          serverName: draftData.albumConfig.coverProjectServerName || ''
        });
      }
      if (draftData.albumConfig.panels) {
        for (const panel of draftData.albumConfig.panels) {
          if (panel.projectId) {
            projects.push({ id: panel.projectId, serverName: panel.projectServerName || '' });
          }
        }
      }
    }
    if (draftData.productConfig?.projectId) {
      projects.push({
        id: draftData.productConfig.projectId,
        serverName: draftData.productConfig.projectServerName || ''
      });
    }
    // Extra designs of a standalone product (the first one is the singular
    // projectId above — deduped by id below).
    for (const ref of draftData.productConfig?.projects ?? []) {
      if (ref.projectId) {
        projects.push({ id: ref.projectId, serverName: ref.serverName || '' });
      }
    }
    if (draftData.productConfig?.panels) {
      for (const panel of draftData.productConfig.panels) {
        if (panel.projectId) {
          projects.push({ id: panel.projectId, serverName: panel.projectServerName || '' });
        }
      }
    }
    formData.append('projects', JSON.stringify([...new Set(projects.map(p => p.id))]));

    // Collect file IDs from draft data (uploaded photos for personalizations)
    const fileIds: number[] = [];
    const collectFromFinishFiles = (finishFiles?: DraftFinishFiles) => {
      if (!finishFiles) return;
      for (const arr of Object.values(finishFiles)) {
        for (const f of arr) {
          if (f.id && f.id > 0) fileIds.push(f.id);
        }
      }
    };
    const collectFromArray = (files?: DraftFinishFile[]) => {
      if (!files) return;
      for (const f of files) {
        if (f.id && f.id > 0) fileIds.push(f.id);
      }
    };
    if (draftData.albumConfig) {
      collectFromFinishFiles(draftData.albumConfig.albumPagesFinishFiles);
      collectFromArray(draftData.albumConfig.uploadedPhotos);
      collectFromFinishFiles(draftData.albumConfig.cover?.finishFiles);
      draftData.albumConfig.cover?.panels?.forEach(p => {
        collectFromFinishFiles(p.finishFiles);
        collectFromFinishFiles(p.selectedProduct?.finishFiles);
      });
      draftData.albumConfig.panels?.forEach(p => {
        collectFromFinishFiles(p.finishFiles);
        collectFromFinishFiles(p.selectedProduct?.finishFiles);
      });
    }
    if (draftData.productConfig) {
      collectFromFinishFiles(draftData.productConfig.finishFiles);
      collectFromArray(draftData.productConfig.uploadedPhotos);
      draftData.productConfig.panels?.forEach(p => {
        collectFromFinishFiles(p.finishFiles);
        collectFromFinishFiles(p.selectedProduct?.finishFiles);
      });
    }
    const uniqueFileIds = [...new Set(fileIds)];
    if (uniqueFileIds.length > 0) {
      formData.append('files', JSON.stringify(uniqueFileIds));
    }

    formData.append('favorite', String(favorite));

    if (draftData.draftThumbnail?.startsWith('data:image/')) {
      // Strip the data URL prefix (e.g., "data:image/jpeg;base64,") — send raw base64 only
      const thumbData = draftData.draftThumbnail.replace(/^data:image\/[^;]+;base64,/, '');
      formData.append('thumb', thumbData);
    } else if (draftData.draftThumbnail) {
      console.warn('[DraftService] Skipping non-data: draftThumbnail (would upload garbage):', draftData.draftThumbnail.substring(0, 80));
    }

    return formData;
  }

  /**
   * Load a draft order from the server
   * @param draftInfo - Draft metadata (contains serverName for retrieval)
   * @param opts - Optional behavior flags. `bustCache: true` appends a
   *   cache-bust query param to the .order URL so the browser HTTP cache
   *   can't return a stale snapshot — used by the preview pipeline.
   * @returns Observable<DraftData> - Complete draft configuration
   */
  loadDraft(draftInfo: DraftInfo, opts?: LoadDraftOptions): Observable<DraftData> {
    // bustCache defaults to TRUE — the .order URL is stable across saves,
    // so without a cache-bust the browser can serve a pre-save snapshot
    // and the customer sees the project revert after a hard refresh.
    const bust = opts?.bustCache !== false;
    console.log('[DraftService] Loading draft:', draftInfo.id, 'url:', draftInfo.url, 'bustCache:', bust);

    if (draftInfo.url) {
      // Fetch the .order file directly from storage
      const url = bust ? this.pathService.withCacheBust(draftInfo.url) : draftInfo.url;
      return this.http.get<DraftData>(url).pipe(
        map(data => {
          console.log('[DraftService] Draft loaded from .order URL:', data);
          return data;
        })
      );
    }

    // No URL available — get metadata from API first (which includes the .order URL), then fetch it
    return this.api.get<any>('/drafts/' + draftInfo.id).pipe(
      switchMap(meta => {
        const orderUrl = meta?.url;
        if (orderUrl) {
          const finalUrl = bust ? this.pathService.withCacheBust(orderUrl) : orderUrl;
          console.log('[DraftService] Got .order URL from API metadata:', finalUrl);
          return this.http.get<DraftData>(finalUrl);
        }
        // If API returns the draft data directly (legacy), use it as-is
        console.log('[DraftService] No .order URL in metadata, using response as draft data');
        return of(meta as DraftData);
      }),
      map(data => {
        console.log('[DraftService] Draft loaded:', data);
        return data;
      })
    );
  }

  /**
   * Fetch a single draft's metadata by id (no .order body). Used by routes
   * that carry only `draftId` to resolve serverName / originalName / url /
   * favorite / productTypeId without redundant URL params.
   */
  getDraftMeta(draftId: number): Observable<DraftInfo> {
    return this.api.get<any>(`/drafts/${draftId}`).pipe(
      map(item => ({
        ...item,
        originalName: item.originalName || item.name,
      } as DraftInfo))
    );
  }

  /**
   * Get list of draft orders for the current customer
   * @returns Observable<DraftInfo[]> - List of draft metadata
   */
  listDrafts(): Observable<DraftInfo[]> {
    return this.api.get<any>('/drafts').pipe(
      map(response => {
        const items = response?.items || (Array.isArray(response) ? response : []);
        const drafts: DraftInfo[] = items.map((item: any) => this.mapDraftInfo(item));
        console.log('[DraftService] Drafts list:', drafts);
        return drafts;
      })
    );
  }

  /**
   * Map a raw draft-list item onto DraftInfo plus the runtime display fields the
   * boards read (originalName, creation/lastModified date strings, thumbnail).
   * Public so the Album Design area maps its list items identically instead of
   * duplicating this field mapping + date formatting (keeping the two boards in
   * sync on date/thumbnail rendering).
   */
  mapDraftInfo(item: any): DraftInfo {
    return {
      ...item,
      originalName: item.originalName || item.name,
      creationDateString: item.creationDateString || this.formatApiDate(item.creationDate),
      lastModifiedDateString: item.lastModifiedDateString || this.formatApiDate(item.lastModificationDate),
      thumbnail: item.thumbnail || item.thumbnailUrl || null,
    } as DraftInfo;
  }

  private formatApiDate(dateStr: string | null | undefined): string {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      const pad = (n: number) => n.toString().padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    } catch {
      return dateStr || '';
    }
  }

  /**
   * Delete a draft order
   * @param draftId - Draft ID to delete
   * @returns Observable<boolean> - Success status
   */
  deleteDraft(draftId: number): Observable<boolean> {
    return this.api.delete<string>('/drafts/' + draftId).pipe(
      map(result => {
        console.log('[DraftService] Delete draft result:', result);
        return result === 'OK';
      })
    );
  }

  /**
   * Update draft favorite flag
   * @param draftId - Draft ID
   * @param favorite - Favorite flag
   * @returns Observable<boolean> - Success status
   */
  updateDraftFavorite(draftId: number, favorite: boolean): Observable<boolean> {
    return this.api.patch<{ success?: boolean }>('/drafts/' + draftId + '/favorite', { favorite }).pipe(
      map(data => {
        return data?.success === true;
      })
    );
  }

  /**
   * Rename a draft order
   * @param draftId - Draft ID
   * @param newName - New draft name
   * @returns Observable<boolean> - Success status
   */
  renameDraft(draftId: number, newName: string): Observable<boolean> {
    return this.api.patch<{ success?: boolean; id?: number }>('/drafts/' + draftId + '/rename', { name: newName }).pipe(
      map(data => {
        console.log('[DraftService] Rename draft result:', data);
        // The v2 API returns the updated draft object ({ id, name, ... }); the
        // older API returned { success: true }. A 2xx reaching here already means
        // success (non-2xx goes to the error channel), so accept either shape.
        return !!data && (data.success === true || data.id != null);
      })
    );
  }

}
